/**
 * VisualArtifactRenderer
 *
 * Shared renderer for Losi visual artifact payloads.
 *
 * Primary responsibilities:
 * - Select the correct viewer for each visual artifact type
 * - Keep chat, annotations, and canvas rendering aligned
 *
 * Dependencies:
 * - Visual artifact types
 * - Visual artifact viewer components
 */

'use client'

import dynamic from 'next/dynamic'
import type { VisualArtifact } from '@/lib/visual-artifacts'
import AddressHeatmapViewer from '@/components/visual-artifacts/address-heatmap-viewer'
import BlueprintViewer from '@/components/visual-artifacts/blueprint-viewer'
import RouteTerritoryMapViewer from '@/components/visual-artifacts/route-territory-map-viewer'
import { SchematicViewer } from '@/components/visual-artifacts/schematic-viewer'
// three.js (~210 KB gz) loads only when a 3D artifact is actually rendered
const ThreeDModelViewer = dynamic(
  () =>
    import('@/components/visual-artifacts/three-d-model-viewer').then(
      (module) => module.ThreeDModelViewer
    ),
  { loading: () => null }
)

type VisualArtifactRendererProps = {
  artifact: VisualArtifact
  className?: string
}

export function VisualArtifactRenderer({ artifact, className }: VisualArtifactRendererProps) {
  if (artifact.type === '3d-model') {
    return (
      <ThreeDModelViewer
        assetUrl={artifact.data.assetUrl}
        className={className}
        downloadFileName={`${artifact.title}.${artifact.data.format}`}
        exportMetadata={artifact.data.exportMetadata}
        format={artifact.data.format}
        bounds={artifact.data.bounds}
        title={artifact.title}
        unit={artifact.data.unit}
      />
    )
  }

  if (artifact.type === 'schematic') {
    return <SchematicViewer artifact={artifact} className={className} />
  }

  if (artifact.type === 'address-map' || artifact.type === 'heatmap') {
    return <AddressHeatmapViewer artifact={artifact} className={className} />
  }

  if (artifact.type === 'route-map' || artifact.type === 'territory-map') {
    return <RouteTerritoryMapViewer artifact={artifact} className={className} />
  }

  if (artifact.type === 'blueprint') {
    return <BlueprintViewer artifact={artifact} className={className} />
  }

  return null
}
